import { useTheme } from '@emotion/react';
import { faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type Condition, useQuestionBuilder } from 'lib-node-question-builder';
import { get, isEqual } from 'lodash';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import type {
  DtoIdentitiesObfuscatedLoanIdentifier,
  DtoV1Borrower,
} from '~/__generated__/api';
import Button from '~/common/components/Buttons/Button/index';
import Flex from '~/common/components/Flex';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import Text from '~/common/components/Texts/Text';
import {
  LOAN_PRODUCT_TYPE_BY_NAME,
  US_STATES_LABEL_VALUE_ARRAY,
} from '~/common/constants';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import type { ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';
import { commonKeys as incomeCalculatorKeys } from '~/pages/IncomeCalculator/utils';
import Loan1003FormBuilderWrapper from '~/pages/Loan1003DataGrids/common/components/Loan1003FormBuilderWrapper';

import { READONLY_CONDITION, formatName } from '../../common/utils/utils';
import type { IncomeRow } from '../types';
import Loan1003FormContainer from './../../common/components/Loan1003FormContainer';
import Loan1003FormSection from './../../common/components/Loan1003FormSection';
import CpaInformationFieldArray from './CpaInformationFieldArray';
import IncomeTypeFieldArray from './IncomeTypeFieldArray';
import {
  createOnChangeHandler,
  newCpaInformationObject,
  newEmploymentDetailsObject,
} from './income1003FormListeners';
import {
  calculateElapsedYearsMonths,
  getIncomeDetailsType,
} from './income1003Utils';

const getDefaultValues = (
  rowToEdit: IncomeRow,
  borrower: DtoV1Borrower | undefined,
  ids: DtoIdentitiesObfuscatedLoanIdentifier,
  timeElapsed: ReturnType<typeof calculateElapsedYearsMonths>,
) => {
  return {
    borrowerName: rowToEdit.borrowerName,
    customerRecordId: rowToEdit.customerRecordId,
    currentEmployment: rowToEdit.currentEmployment === 'Y',
    employerId: rowToEdit.employerId,
    lenderDatabaseId: rowToEdit.customerLenderDatabaseId,
    incomeSource:
      rowToEdit.isW2Employment == 2 ? 'Non-Employment' : 'Employment',
    employmentType: [
      ...(rowToEdit.primaryEmployment ? ['primaryEmployment'] : []),
      ...(rowToEdit.selfEmployed ? ['Self-Employment'] : []),
    ],
    borrowerSSN: borrower?.socialSecurityNumber,
    employerPhone: rowToEdit.employerPhone,
    employmentDetails: rowToEdit.monthlyIncomeDetails?.map((item) => {
      return {
        incomeIdentifier: {
          obfuscatedLenderDatabaseId: ids.obfuscatedLenderDatabaseId,
          customerRecordId: rowToEdit.customerRecordId,
          employerId: rowToEdit.employerId,
          sequenceNumber: item.sequenceNumber,
        },
        incomeType: getIncomeDetailsType(item.type),
        monthlyNetIncome: item.amount,
        period: 0,
        taxable: item.taxable,
        hoursPerWeek: item.hoursPerWeek,
        incomeVerifiedDate: item.incomeVerifiedDate,
        incomeVerifiedFlag: '',
      };
    }),
    employerName: rowToEdit.employerName,
    empByPartyToTranFlag: rowToEdit.empByPartyToTranFlag,
    primaryEmployment: rowToEdit.primaryEmployment,
    selfEmployed: rowToEdit.selfEmployed,
    percentBusinessOwned: rowToEdit.percentBusinessOwned,
    lessThan25PercentOwnershipFlag:
      rowToEdit.lessThan25PercentOwnershipFlag === 1,
    country: rowToEdit.country,
    employerAddress: {
      addressLine1: rowToEdit.employerAddress?.addressLine1,
      city: rowToEdit.employerAddress?.city,
      state: rowToEdit.employerAddress?.state,
      zipCode: rowToEdit.employerAddress?.zipCode,
    },
    title: rowToEdit.title,
    typeOfBusiness: rowToEdit.typeOfBusiness,
    startDate: rowToEdit.startDate,
    endDate: rowToEdit.endDate == '1/1/0001' ? undefined : rowToEdit.endDate,
    yearsOnJob: timeElapsed.years,
    monthsOnJob: timeElapsed.months,
    verifiedDate: rowToEdit.verifiedDate,
    cpaInformation: rowToEdit.cpaInformation?.map((item) => {
      return {
        customerCpaDetailId: item.customerCpaDetailId ?? 0,
        lenderDatabaseId: item.lenderDatabaseId ?? 0,
        customerRecordId: item.customerRecordId ?? 0,
        employerId: item.employerId ?? 0,
        cpaFirstName: item.cpaFirstName ?? '',
        cpaMiddleName: item.cpaMiddleName ?? '',
        cpaLastName: item.cpaLastName ?? '',
        cpaCompanyName: item.cpaCompanyName ?? '',
      };
    }),
  };
};

export type Income1003FormValues = ReturnType<typeof getDefaultValues>;

const INCOME_SOURCE_IS_EMPLOYMENT_CONDITION: Condition = {
  when: 'incomeSource',
  is: 'Employment',
  dataSource: 'formData',
};

const CALCULATION_AFFECTING_FIELD_PATHS = [
  'customerRecordId',
  'currentEmployment',
  'incomeSource',
  'employmentDetails',
  'employmentType',
  'percentBusinessOwned',
] as const;

type SaveIncomeRequest = Parameters<
  typeof typedApiClient.v1.underwritingSummaryIncomeUpsertUnderwritingSummaryIncome
>[0];

type IncomeSummariesFormProps = {
  rowToEdit: IncomeRow;
  onClose?: () => void;
  onSave?: () => void;
  allBorrowerOptions: DtoV1Borrower[];
  isWriteAccessDisabled?: boolean;
  obfuscatedIds: ObfuscatedLoanIdentifier;
};

export default function IncomeSummariesForm({
  rowToEdit,
  onClose,
  onSave,
  allBorrowerOptions,
  isWriteAccessDisabled,
  obfuscatedIds,
}: Readonly<IncomeSummariesFormProps>) {
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const featureFlags = useNavFeatureFlags();
  const productType = useGetLoanSummary().data?.loanInfo?.loanProductType;
  const yearsMonthsElapsed = calculateElapsedYearsMonths(
    rowToEdit.startDate,
    rowToEdit.endDate,
  );
  const currentBorrower = allBorrowerOptions.find(
    (borrower) => borrower.customerRecordId === rowToEdit.customerRecordId,
  );
  const defaultFormValues = getDefaultValues(
    rowToEdit,
    currentBorrower,
    obfuscatedIds,
    yearsMonthsElapsed,
  );

  const initialEmployerNameRef = useRef(rowToEdit.employerName);
  const initialFormValuesRef = useRef(defaultFormValues);

  const { mutate: makeUpsertCall, isLoading } = useMutation({
    mutationFn: async (saveObject: SaveIncomeRequest) => {
      return await typedApiClient.v1.underwritingSummaryIncomeUpsertUnderwritingSummaryIncome(
        saveObject,
      );
    },
    onSuccess: async () => {
      onSave();
      showSnackbar({
        severity: 'success',
        message: 'Saved Successfully',
        duration: 5000,
      });
      dispatch(setLoanSummaryUpsertOrDelete(true));
      await queryClient.invalidateQueries([
        ...incomeCalculatorKeys.all,
        ...incomeCalculatorKeys.withIdentifier({
          obfuscatedLenderDatabaseId: currentBorrower?.lenderdatabaseId,
          customerRecordId: rowToEdit.customerRecordId,
          employerId: rowToEdit.employerId,
        }),
      ]);
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Record was not able to be saved.',
        duration: 5000,
      });
    },
  });

  const { form, formApi, handleSubmit } = useQuestionBuilder({
    formData: defaultFormValues,
    screenData: {
      isWriteAccessDisabled,
    },
    config: { defaultPadding: false },
    listeners: {
      onChange: createOnChangeHandler({
        obfuscatedIds,
        allBorrowerOptions,
        rowToEdit,
      }),
    },
    hooks: {
      onSubmit: (data) => {
        const selfEmployed =
          data.employmentType?.includes('Self-Employment') ?? false;
        const primaryEmployment =
          data.employmentType?.includes('primaryEmployment') ?? false;
        const isW2Employment = data.incomeSource === 'Employment';

        let employmentDetailsArray = [];
        if (data.employmentDetails?.length > 0) {
          employmentDetailsArray = data.employmentDetails.map((detail, i) => {
            if (!detail.incomeIdentifier) {
              return {
                incomeIdentifier: {
                  obfuscatedLenderDatabaseId:
                    obfuscatedIds.obfuscatedLenderDatabaseId,
                  customerRecordId: +data.customerRecordId,
                  employerId: rowToEdit.employerId,
                  sequenceNumber: i + 1 * 10,
                },
                incomeType: detail.incomeType,
                monthlyNetIncome: +detail.monthlyNetIncome,
                incomeVerifiedDate: detail.incomeVerifiedDate,
              };
            }
            return {
              ...detail,
              incomeIdentifier: {
                ...detail.incomeIdentifier,
                customerRecordId: +data.customerRecordId,
              },
            };
          });
        }
        let cpaInformationArray = [];
        if (data.cpaInformation?.length > 0) {
          cpaInformationArray = data.cpaInformation.map((cpa) => {
            return {
              customerCpaDetailId: cpa.customerCpaDetailId ?? 0,
              lenderDatabaseId: cpa.lenderDatabaseId ?? 1,
              customerRecordId: +data.customerRecordId,
              employerId: data.employerId,
              cpaFirstName: cpa.cpaFirstName,
              cpaMiddleName: cpa.cpaMiddleName,
              cpaLastName: cpa.cpaLastName,
              cpaCompanyName: cpa.cpaCompanyName,
            };
          });
        }

        const shouldIncludeCpaInformation =
          isW2Employment &&
          data.currentEmployment === true &&
          selfEmployed &&
          cpaInformationArray.length > 0;

        const shouldInactivateNonApplicableCalculations =
          CALCULATION_AFFECTING_FIELD_PATHS.some(
            (path) =>
              !isEqual(
                get(initialFormValuesRef.current, path),
                get(data, path),
              ),
          );

        const saveObject = {
          country: data.country ?? null,
          isCurrentEmployment: data.currentEmployment === true,
          obfuscatedLoanIdentifier: obfuscatedIds,
          customerRecordIdOriginal:
            rowToEdit.customerRecordIdOriginal === +data.customerRecordId
              ? null
              : rowToEdit.customerRecordIdOriginal,
          incomeIdentifier: {
            obfuscatedLenderDatabaseId:
              obfuscatedIds.obfuscatedLenderDatabaseId,
            customerRecordId: +data.customerRecordId,
            employerId: data.employerId ?? 0,
          },
          empByPartyToTranFlag: data.empByPartyToTranFlag ? 1 : 0,
          employerAddress: data.employerAddress ?? {
            addressLine1: '',
            city: '',
            state: '',
            zipCode: '',
          },
          employerName: data.employerName ?? null,
          employerPhone: data.employerPhone ?? '',
          employmentDetails: employmentDetailsArray,
          beginDate: data.startDate ?? '',
          endDate: data.endDate ?? null,
          percentageOwnership: data.percentBusinessOwned ?? null,
          primaryEmployment: primaryEmployment ? 'Y' : 'N',
          selfEmployed,
          title: data.title ?? '',
          typeOfBusiness: data.typeOfBusiness ?? '',
          verifiedDate: data.verifiedDate ?? '',
          cpaInformation: shouldIncludeCpaInformation
            ? cpaInformationArray
            : [],
          isW2Employment,
          shouldInactivateNonApplicableCalculations,
          shouldTriggerUwaiEntityMatching:
            data.employerName !== initialEmployerNameRef.current,
        };

        makeUpsertCall(saveObject);
      },
    },
  });

  const theme = useTheme();

  const closeCpaSection = useCallback(() => {
    formApi.setFieldValue('cpaInformation', []);
  }, [formApi]);

  return (
    <form.Provider>
      <Loan1003FormContainer
        isLoading={isLoading}
        onCloseClicked={onClose}
        toggleErrors={() => {}}
        onSaveClicked={handleSubmit}
        readOnlyMode={isWriteAccessDisabled}
      >
        <Loan1003FormSection title="Income Information">
          <Loan1003FormBuilderWrapper>
            <form.Grid
              name="income information grid"
              columns={4}
              style={{
                padding: 0,
              }}
            >
              <form.GridItem>
                <form.Select
                  name="customerRecordId"
                  label="Associated Borrower"
                  rules={{
                    required: true,
                  }}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                  }}
                  placeholder="Select Borrower"
                  options={allBorrowerOptions?.map((borrower) => {
                    return {
                      value: borrower.customerRecordId,
                      label: formatName(
                        borrower.firstName,
                        borrower.lastName,
                        borrower.middleName,
                        borrower.suffixName,
                      ),
                    };
                  })}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Checkbox
                  name="currentEmployment"
                  label="Current"
                  style={{ marginTop: '1.25rem', marginLeft: '1rem' }}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                  }}
                />
              </form.GridItem>
            </form.Grid>
            <form.Radio
              name="incomeSource"
              label="Income Source"
              style={{ padding: '1rem 0 0 0' }}
              hasBottomBorder={false}
              conditions={{
                isDisabled: READONLY_CONDITION,
              }}
              options={[
                {
                  label: 'Employment',
                  value: 'Employment',
                },
                {
                  label: 'Non-Employment',
                  value: 'Non-Employment',
                },
              ]}
            />
            <IncomeTypeFieldArray
              newAppendObject={newEmploymentDetailsObject(
                obfuscatedIds,
                rowToEdit,
              )}
              form={form}
            />
          </Loan1003FormBuilderWrapper>
        </Loan1003FormSection>
        <Loan1003FormSection
          title="Employment Information"
          customMarginTop="1.25rem"
        >
          <Loan1003FormBuilderWrapper>
            <form.Grid name="employment information grid" columns={4}>
              <form.GridItem colSpan={2}>
                <form.Text
                  name="employerName"
                  label="Employer Or Description"
                  rules={{
                    required: true,
                  }}
                  style={{ padding: 0 }}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Checkbox
                  name="empByPartyToTranFlag"
                  label={
                    featureFlags.IncomeConvNonArmsLengthGuidelineEnabled &&
                    productType === LOAN_PRODUCT_TYPE_BY_NAME.CONVENTIONAL
                      ? 'Employed by Family or Interested Party'
                      : 'Employed by Family'
                  }
                  style={{ marginTop: '1.25rem' }}
                  conditions={{
                    isVisible: {
                      when: 'incomeSource',
                      is: 'Employment',
                      dataSource: 'formData',
                    },
                    isDisabled: READONLY_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem colStart={1}>
                <form.CheckboxGroup
                  name="employmentType"
                  label="Employment Type"
                  orientation="horizontal"
                  options={[
                    { value: 'primaryEmployment', label: 'Primary' },
                    {
                      value: 'Self-Employment',
                      label: 'Self-Employment',
                      conditions: {
                        isVisible: {
                          when: 'incomeSource',
                          is: 'Employment',
                          dataSource: 'formData',
                        },
                      },
                    },
                  ]}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Percentage
                  name="percentBusinessOwned"
                  label="% Owned"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: {
                      and: [
                        {
                          when: 'employmentType',
                          contains: 'Self-Employment',
                          dataSource: 'formData',
                        },
                        {
                          when: 'incomeSource',
                          is: 'Employment',
                          dataSource: 'formData',
                        },
                      ],
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Checkbox
                  name="lessThan25PercentOwnershipFlag"
                  label="Less than 25% Ownership"
                  style={{ marginTop: '1.25rem' }}
                  conditions={{
                    isDisabled: true,
                    isVisible: {
                      and: [
                        {
                          when: 'employmentType',
                          contains: 'Self-Employment',
                          dataSource: 'formData',
                        },
                        {
                          when: 'incomeSource',
                          is: 'Employment',
                          dataSource: 'formData',
                        },
                      ],
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem colStart={1} colSpan={2}>
                <form.Text
                  name="employerAddress.addressLine1"
                  label="Address Line 1"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: {
                      when: 'incomeSource',
                      is: 'Employment',
                      dataSource: 'formData',
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem colStart={1}>
                <form.Text
                  name="employerAddress.city"
                  label="City"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Select
                  name="employerAddress.state"
                  label="State"
                  options={US_STATES_LABEL_VALUE_ARRAY}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="country"
                  label="Country"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="employerAddress.zipCode"
                  label="Zip Code"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="title"
                  label="Job Title"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="typeOfBusiness"
                  label="Type of Business"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.PhoneNumber
                  name="employerPhone"
                  label="Employer Home Phone"
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
              <form.GridItem colStart={1}>
                <form.Date
                  name="startDate"
                  label="Start Date"
                  rules={{
                    required: true,
                  }}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isVisible: {
                      or: [
                        INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                        {
                          when: 'currentEmployment',
                          is: false,
                          dataSource: 'formData',
                        },
                      ],
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Date
                  name="endDate"
                  label="End Date"
                  rules={{
                    onSubmit: {
                      message: 'End date must be later than start date',
                      type: 'date',
                      after: { field: 'startDate' },
                    },
                  }}
                  conditions={{
                    isDisabled: READONLY_CONDITION,
                    isRequired: {
                      when: 'currentEmployment',
                      is: false,
                      dataSource: 'formData',
                    },
                    isVisible: {
                      or: [
                        INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                        {
                          when: 'currentEmployment',
                          is: false,
                          dataSource: 'formData',
                        },
                      ],
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="yearsOnJob"
                  label="Years on Job"
                  conditions={{
                    isDisabled: true,
                    isVisible: {
                      or: [
                        INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                        {
                          when: 'currentEmployment',
                          is: false,
                          dataSource: 'formData',
                        },
                      ],
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem>
                <form.Text
                  name="monthsOnJob"
                  label="Months on Job"
                  conditions={{
                    isDisabled: true,
                    isVisible: {
                      or: [
                        INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                        {
                          when: 'currentEmployment',
                          is: false,
                          dataSource: 'formData',
                        },
                      ],
                    },
                  }}
                />
              </form.GridItem>
              <form.GridItem colStart={1}>
                <form.Date
                  name="verifiedDate"
                  label="VVOE Date/VOB Date"
                  conditions={{
                    isDisabled: true,
                    isVisible: INCOME_SOURCE_IS_EMPLOYMENT_CONDITION,
                  }}
                />
              </form.GridItem>
            </form.Grid>
            <form.Subscribe
              selector={(s) => ({
                showCpaButton:
                  s.values.incomeSource === 'Employment' &&
                  s.values.employmentType?.includes('Self-Employment') &&
                  s.values.currentEmployment === true,
                showCPASection: (s.values.cpaInformation?.length ?? 0) > 0,
              })}
            >
              {({ showCpaButton, showCPASection }) => {
                if (!showCpaButton) {
                  return null;
                }

                return (
                  <Flex justifyContent="flex-start">
                    <Button
                      background={'none'}
                      disabled={isWriteAccessDisabled}
                      onClick={() => {
                        if (showCPASection) {
                          closeCpaSection();
                        } else {
                          formApi.setFieldValue('cpaInformation', [
                            newCpaInformationObject(),
                          ]);
                        }
                      }}
                      padding={'0.25rem'}
                    >
                      <Flex align={'flex-start'} wordBreak={'break-word'}>
                        <FontAwesomeIcon
                          icon={showCPASection ? faMinusSquare : faPlusSquare}
                          color={theme.colors?.blue[600]}
                        />
                        <Text paddingLeft={'1rem'}>
                          {showCPASection ? 'Remove' : 'Add'} CPA Information
                        </Text>
                      </Flex>
                    </Button>
                  </Flex>
                );
              }}
            </form.Subscribe>
          </Loan1003FormBuilderWrapper>
        </Loan1003FormSection>
        <form.Subscribe
          selector={(s) =>
            (s.values.cpaInformation?.length ?? 0) > 0 &&
            s.values.incomeSource === 'Employment' &&
            s.values.currentEmployment &&
            s.values.employmentType?.includes('Self-Employment')
          }
        >
          {(showCpaSection) => {
            if (!showCpaSection) {
              return null;
            }

            return (
              <Loan1003FormSection title="CPA Information">
                <CpaInformationFieldArray form={form} />
              </Loan1003FormSection>
            );
          }}
        </form.Subscribe>
      </Loan1003FormContainer>
    </form.Provider>
  );
}
